import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type React from "react"

import { useScopedLocalStorage } from "@/workspaces/scope"
import type { ColumnDef } from "@coval/ui"

import type { RunRowLike } from "@/types/RunRow"

import type { RunResultsMetricRenderAdaptersById } from "../types"

export interface RunResultsTableColumn {
  key: string
  /** Metric and explanation keys are arbitrary names, unlike fixed columns. */
  kind?: "metric" | "explanation"
  label: string
  width?: string
  minWidth?: string
  maxWidth?: string
  fullLabel?: string
}

const COLUMN_VISIBILITY_STORAGE_KEY = "table-columns:run-results"
const COLUMN_ORDER_STORAGE_KEY = "table-columns:run-results:order"

const asKeyList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((key): key is string => typeof key === "string")
    : []
const DEFAULT_HIDDEN_COLUMNS = new Set(["agentId"])

const isVisibleByDefault = (key: string) => !DEFAULT_HIDDEN_COLUMNS.has(key)

const metricKeysOf = (columns: RunResultsTableColumn[]) =>
  columns.filter((column) => column.kind === "metric").map((col) => col.key)

/**
 * Re-sorts the metric keys inside a user's column order to follow
 * `metricKeys`, leaving every other key in its slot. Lets a metric order
 * chosen outside the table move columns the user has already dragged.
 */
export function followMetricOrder(
  columnOrder: readonly string[],
  metricKeys: readonly string[],
): string[] {
  const metricSet = new Set(metricKeys)
  const ordered = new Set(columnOrder)
  const sortedMetrics = metricKeys.filter((key) => ordered.has(key))
  let next = 0
  return columnOrder.map((key) =>
    metricSet.has(key) ? sortedMetrics[next++] : key,
  )
}

const sameRelativeOrder = (
  previous: readonly string[],
  current: readonly string[],
) => {
  const shared = new Set(current)
  const kept = previous.filter((key) => shared.has(key))
  const keptSet = new Set(kept)
  return current
    .filter((key) => keptSet.has(key))
    .every((key, index) => key === kept[index])
}

/**
 * Adds every default key missing from the user's order right after the key
 * that precedes it in `defaultOrder`, so a column that appears after a drag
 * lands at its designed slot rather than at the end.
 */
export function placeNewColumns(
  columnOrder: readonly string[],
  defaultOrder: readonly string[],
): string[] {
  const result = [...columnOrder]
  const placed = new Set(columnOrder)
  defaultOrder.forEach((key, index) => {
    if (placed.has(key)) {
      return
    }
    const previous = defaultOrder
      .slice(0, index)
      .findLast((candidate) => placed.has(candidate))
    result.splice(previous ? result.indexOf(previous) + 1 : 0, 0, key)
    placed.add(key)
  })
  return result
}

const METRIC_COLUMN_MIN_WIDTH_PX = 100
const DEFAULT_COLUMN_MIN_WIDTH_PX = 96
const COLUMN_MIN_WIDTHS_PX: Record<string, number> = {
  actions: 70,
  compareBy: 120,
  errorStatus: 44,
  expectedBehaviors: 160,
  expectedOutputJson: 160,
  index: 44,
  inputStr: 120,
  mutationName: 120,
  simulationOutputId: 56,
  transcript: 160,
}

export function parsePixelWidth(value: string | undefined): number | null {
  if (!value) {
    return null
  }

  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/)
  if (!match) {
    return null
  }

  return Number(match[1])
}

export function getRunResultsColumnMinWidthPx(
  column: RunResultsTableColumn,
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById,
): number {
  if (column.key in metricRenderAdaptersById) {
    return METRIC_COLUMN_MIN_WIDTH_PX
  }

  return (
    COLUMN_MIN_WIDTHS_PX[column.key] ??
    parsePixelWidth(column.minWidth) ??
    parsePixelWidth(column.width) ??
    DEFAULT_COLUMN_MIN_WIDTH_PX
  )
}

export function getRunResultsColumnMinWidthStyle(
  column: RunResultsTableColumn,
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById,
): string {
  if (column.key in metricRenderAdaptersById) {
    return `${METRIC_COLUMN_MIN_WIDTH_PX}px`
  }

  return (
    column.minWidth ??
    column.width ??
    `${getRunResultsColumnMinWidthPx(column, metricRenderAdaptersById)}px`
  )
}

interface UseRunResultsTableColumnsParams {
  allColumns: RunResultsTableColumn[]
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  /** A public shared link keeps visibility in memory, apart from the viewer's organization. */
  isShared?: boolean
  /**
   * The metric column order comes from outside the table, such as a report's
   * metric settings, so a stored order follows it from the first render.
   */
  metricOrderIsControlled?: boolean
}

interface RunResultsTableColumnsController {
  columnToggleDefs: ColumnDef<RunRowLike>[]
  columnVisibility: Record<string, boolean>
  handleColumnVisibilityChange: (visibility: Record<string, boolean>) => void
  handleResetColumns: () => void
  columnOrder: string[]
  draggedColumn: string | null
  effectiveVisibleColumns: Set<string>
  effectiveColumnOrder: string[]
  visibleOrderedColumns: RunResultsTableColumn[]
  tableMinWidthPx: number
  setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>
  handleDragStart: (e: React.DragEvent, columnKey: string) => void
  handleDragOver: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent, targetColumnKey: string) => void
  handleDragEnd: () => void
}

/**
 * Owns column visibility, ordering, and drag-and-drop reordering for the run
 * results table. Visibility is stored as overrides of the defaults (every
 * column but Agent) in workspace-scoped `localStorage`, so a hidden column
 * stays hidden on every run until the user shows it again. Columns a run does
 * not have keep their stored override for the runs that do. Column order is
 * stored the same way, so a drag sticks across pages and runs. A shared link
 * neither reads nor writes either.
 */
export function useRunResultsTableColumns({
  allColumns,
  metricRenderAdaptersById,
  isShared = false,
  metricOrderIsControlled = false,
}: UseRunResultsTableColumnsParams): RunResultsTableColumnsController {
  const [visibilityOverrides, setVisibilityOverrides] = useScopedLocalStorage<
    Record<string, boolean>
  >(isShared ? null : COLUMN_VISIBILITY_STORAGE_KEY, {})
  const [storedOrder, setStoredOrder] = useScopedLocalStorage<string[]>(
    isShared ? null : COLUMN_ORDER_STORAGE_KEY,
    [],
  )
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null)

  const allKeys = useMemo(
    () => allColumns.map((column) => column.key),
    [allColumns],
  )
  const columnOrder = useMemo(() => {
    const present = new Set(allKeys)
    return asKeyList(storedOrder).filter((key) => present.has(key))
  }, [allKeys, storedOrder])

  // Updates see only this run's columns. Stored positions of columns the run
  // does not have are kept for the runs that do; an empty order resets all.
  const setColumnOrder = useCallback<
    React.Dispatch<React.SetStateAction<string[]>>
  >(
    (update) => {
      setStoredOrder((previous) => {
        const stored = asKeyList(previous)
        const present = new Set(allKeys)
        const current = stored.filter((key) => present.has(key))
        const next = typeof update === "function" ? update(current) : update
        if (next.length === 0) {
          return []
        }
        const nextKeys = new Set(next)
        return [
          ...next,
          ...stored.filter((key) => !present.has(key) && !nextKeys.has(key)),
        ]
      })
    },
    [allKeys, setStoredOrder],
  )

  const metricKeys = useMemo(() => metricKeysOf(allColumns), [allColumns])
  // A controlled order starts from the stored metric order, so the first
  // effect run re-sorts it when the two disagree.
  const baselineMetricKeysRef = useRef<readonly string[]>(
    metricOrderIsControlled ? columnOrder : metricKeys,
  )
  useEffect(() => {
    const baseline = baselineMetricKeysRef.current
    baselineMetricKeysRef.current = metricKeys
    if (!sameRelativeOrder(baseline, metricKeys)) {
      setColumnOrder((previous) => followMetricOrder(previous, metricKeys))
    }
  }, [metricKeys, setColumnOrder])

  // Stored overrides carry the column kind, so a metric or explanation column
  // can never hide a fixed column that shares its key on another run.
  const overrideId = useCallback(
    (key: string) => {
      const kind = allColumns.find((column) => column.key === key)?.kind
      return `${kind ?? "column"}:${key}`
    },
    [allColumns],
  )

  const effectiveVisibleColumns = useMemo(
    () =>
      new Set(
        allColumns
          .map((column) => column.key)
          .filter(
            (key) =>
              visibilityOverrides[overrideId(key)] ?? isVisibleByDefault(key),
          ),
      ),
    [visibilityOverrides, allColumns, overrideId],
  )

  const effectiveColumnOrder = useMemo(
    () => placeNewColumns(columnOrder, allKeys),
    [columnOrder, allKeys],
  )

  const visibleOrderedColumns = useMemo(
    () =>
      effectiveColumnOrder
        .filter((columnKey) => effectiveVisibleColumns.has(columnKey))
        .map((columnKey) => allColumns.find((col) => col.key === columnKey))
        .filter((col): col is RunResultsTableColumn => col != null),
    [allColumns, effectiveColumnOrder, effectiveVisibleColumns],
  )

  const tableMinWidthPx = useMemo(
    () =>
      visibleOrderedColumns.reduce(
        (total, column) =>
          total +
          getRunResultsColumnMinWidthPx(column, metricRenderAdaptersById),
        0,
      ),
    [metricRenderAdaptersById, visibleOrderedColumns],
  )

  const columnToggleDefs = useMemo<ColumnDef<RunRowLike>[]>(
    () =>
      allColumns.map((column) => ({
        id: column.key,
        header: column.fullLabel || column.label || column.key,
        headerLabel: column.fullLabel || column.label?.trim() || column.key,
      })),
    [allColumns],
  )

  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(
        allColumns.map((column) => [
          column.key,
          effectiveVisibleColumns.has(column.key),
        ]),
      ),
    [allColumns, effectiveVisibleColumns],
  )

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      const changed = Object.entries(visibility).filter(
        ([key, visible]) => visible !== effectiveVisibleColumns.has(key),
      )
      setVisibilityOverrides((previous) => {
        const next = { ...previous }
        for (const [key, visible] of changed) {
          if (visible === isVisibleByDefault(key)) {
            delete next[overrideId(key)]
          } else {
            next[overrideId(key)] = visible
          }
        }
        return next
      })
    },
    [effectiveVisibleColumns, overrideId, setVisibilityOverrides],
  )

  const handleResetColumns = useCallback(() => {
    setVisibilityOverrides({})
    setColumnOrder([])
  }, [setColumnOrder, setVisibilityOverrides])

  const handleDragStart = useCallback(
    (e: React.DragEvent, columnKey: string) => {
      setDraggedColumn(columnKey)
      e.dataTransfer.effectAllowed = "move"
    },
    [],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, targetColumnKey: string) => {
      e.preventDefault()

      if (!draggedColumn || draggedColumn === targetColumnKey) {
        setDraggedColumn(null)
        return
      }

      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder]
        const draggedIndex = newOrder.indexOf(draggedColumn)
        const targetIndex = newOrder.indexOf(targetColumnKey)

        newOrder.splice(draggedIndex, 1)

        const adjustedTargetIndex =
          draggedIndex < targetIndex ? targetIndex - 1 : targetIndex
        newOrder.splice(adjustedTargetIndex, 0, draggedColumn)

        return newOrder
      })

      setDraggedColumn(null)
    },
    [draggedColumn, setColumnOrder],
  )

  const handleDragEnd = useCallback(() => {
    setDraggedColumn(null)
  }, [])

  return {
    columnToggleDefs,
    columnVisibility,
    handleColumnVisibilityChange,
    handleResetColumns,
    columnOrder,
    draggedColumn,
    effectiveVisibleColumns,
    effectiveColumnOrder,
    visibleOrderedColumns,
    tableMinWidthPx,
    setColumnOrder,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  }
}
